import { Link, useParams } from "react-router-dom";
import Section from "../../parts/Section";
import { MaskedTextBlock } from "../../parts/FormBlocks";
import { useEffect, useState } from "react";
import Api from "../../Api";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import LogoColor from "../../images/LogoColor";
import moment from "moment";
import { Client, Quote } from "../../types";

const AuthorizeQuote = () => {
  document.title = "Proposta de segurança eletrônica";

  const { hash } = useParams();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [userDoc, setUserDoc] = useState("");
  const [state, setState] = useState("");

  useEffect(() => {
    Api.externalGetQuoteForAuthorization(hash!).then(({ quote, client }) => {
      setQuote(quote);
      setClient(client);
      setState(
        quote.isAuthorized
          ? "authorized"
          : moment().diff(quote.approvalDate, "days") >= 45
            ? "expired"
            : "pending"
      );
    });
  }, [hash]);

  const authorize = (event: React.FormEvent) => {
    event.preventDefault();
    Api.externalAuthorizeQuote(hash!).then(() => setState("authorized"));
  };

  if (!quote || !client) return <></>;

  const documentHint = [
    client.document.slice(0, 2),
    client.document.slice(2, -2).replace(/[0-9]/g, "_"),
    client.document.slice(-2),
  ].join("");

  return (
    <>
      <div className="p-4 border-b flex flex-col items-center absolute w-full bg-white">
        <LogoColor />
      </div>

      <Section
        show={state === "pending"}
        className="py-6 px-4 flex flex-col gap-4 pt-28"
      >
        <div className="font-bold">{client.name}</div>
        <div>
          Para <strong>autorizar</strong> esta proposta de segurança eletrônica
          por favor confirme o{" "}
          <strong>{client.type === "pf" ? "CPF" : "CNPJ"}</strong> de cadastro.
        </div>
        <Link
          className="underline text-gray-500 hover:text-gray-900"
          to={`/externo/proposta/${hash}`}
          target="_blank"
        >
          Visualizar proposta novamente »
        </Link>{" "}
        <form onSubmit={authorize} className="flex flex-col gap-2">
          <MaskedTextBlock
            label={client.type === "pf" ? "CPF" : "CNPJ"}
            placeholder={documentHint}
            value={userDoc}
            onChange={(value) => setUserDoc(value)}
            mask={
              client.type === "pf" ? "999.999.999-99" : "99.999.999/9999-99"
            }
          />

          <button
            className={classNames("py-2 px-4 font-extrabold font-lg rounded", {
              "text-gray-400 bg-gray-100": userDoc !== client.document,
              "text-white bg-sky-600 hover:bg-sky-500":
                userDoc === client.document,
            })}
            disabled={userDoc !== client.document}
          >
            Autorizar
          </button>
        </form>
      </Section>

      <Section
        show={state === "authorized"}
        className="flex items-center h-screen"
      >
        <div className="flex flex-col items-center w-full gap-4 -mt-16">
          <FontAwesomeIcon
            icon="circle-check"
            className="text-7xl text-sky-700"
          />
          <div className="text-lg font-bold">Proposta autorizada</div>
          <Link
            className="underline text-gray-500 hover:text-gray-900"
            to={`/externo/proposta/${hash}`}
            target="_blank"
          >
            Visualizar »
          </Link>{" "}
        </div>
      </Section>

      <Section
        show={state === "expired"}
        className="pt-32 text-center flex flex-col gap-8"
      >
        <div className="text-xl font-bold">Proposta expirada</div>

        <div className="leading-snug tracking-tight">
          A validade desta proposta já se encerrou. Por favor entre em contato
          para receber uma proposta atualizada:
        </div>

        <div className="leading-snug">
          <div className="font-bold">Email</div>
          <div className="font-mono bg-gray-100 p-2 rounded-lg">
            vendas@alarmsystem.com.br
          </div>
        </div>

        <div className="leading-snug">
          <div className="font-bold">Telefone</div>
          <div className="font-mono bg-gray-100 p-2 rounded-lg">
            27 3232-1000
          </div>
        </div>
      </Section>
    </>
  );
};

export default AuthorizeQuote;
